/**
 * Cart drawer redraw + open for the nested (parent_line_key) protection-plan chain-add.
 *
 * Why this exists: `prosuro-cart-add-nested.js` adds the protection plan via a *second*, separate
 * `/cart/add.js` POST (parent_line_key bundling) after the theme's own add already resolved.
 * Nothing tells the theme's cart drawer a second line was added, and nothing shows it. This file
 * is that missing piece, layered in two tiers:
 *
 *   Tier 1 (preferred): Shopify's standardized storefront actions/events
 *     (`Shopify.actions.openCart`, `shopify:cart:lines-update`) on Online Store 2.0 themes that
 *     support them — see https://shopify.dev/docs/api/storefront-events-and-actions.
 *   Tier 2 (fallback): a manual Section Rendering API swap (`sections` param on the chain POST,
 *     applied by hand) for themes that don't expose the standard actions yet.
 *
 * Load this file BEFORE `prosuro-cart-add-nested.js` — script order matters even with `defer`
 * (deferred scripts still execute in document order relative to each other).
 *
 * Public API: window.ProsuroCartDrawerSync
 *   getCartContext()                          -> 'drawer' | 'page'
 *   isDawnCartDrawerTheme()                  -> true when Dawn-style cart drawer markers present
 *   getCartItemsSectionId(context)             -> string section id (dynamically discovered)
 *   getCartFooterSectionId(context)            -> string section id (dynamically discovered)
 *   buildSectionsList(context)                 -> string[] of section ids to request
 *   applySections(sections)                    -> swap { [sectionId]: html } into the live DOM
 *   openCartDrawer()                           -> Promise; prefers Shopify.actions.openCart()
 *   notifyCartChanged(detail)                  -> best-effort shopify:cart:lines-update dispatch
 *   refreshAndOpenCart({ sections, cart })      -> applySections + notifyCartChanged + openCartDrawer
 *
 * Silence logs: window.__PROSURO_CART_DRAWER_SYNC_DEBUG__ = false
 */
(function () {
  'use strict';

  if (window.__prosuroCartDrawerSyncLoaded) return;
  window.__prosuroCartDrawerSyncLoaded = true;

  function debugEnabled() {
    return window.__PROSURO_CART_DRAWER_SYNC_DEBUG__ !== false;
  }

  function log() {
    if (!debugEnabled()) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[Prosuro cart drawer sync]');
    console.log.apply(console, a);
  }

  function logWarn() {
    if (!debugEnabled()) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[Prosuro cart drawer sync]');
    console.warn.apply(console, a);
  }

  function shopifyThemeLabel() {
    var theme = window.Shopify && window.Shopify.theme;
    if (!theme) return '';
    return String(theme.schema_name || theme.name || '');
  }

  /**
   * Archetype Expanse (including 1.0). Its header cart popup uses the same
   * `#HeaderCart` / `.site-header__cart` nodes as West/Prestige, so the Prestige
   * branch below would otherwise treat Expanse as Prestige, request the whole
   * `header` section, and swap it. That swap drops the open-state class on the
   * popup while a close button that lives outside that node stays on screen.
   * Prestige does not use `.js-drawer-open-cart`. Dawn does not either.
   */
  function isExpanseTheme() {
    if (shopifyThemeLabel().toLowerCase().indexOf('expanse') !== -1) return true;
    if (document.querySelector('.js-drawer-open-cart, [aria-controls="CartDrawer"]')) return true;
    return false;
  }

  /**
   * Expanse reprints the already-open popup from `/cart.js` on `cart:build`.
   * Do not call `Shopify.actions.openCart()` and do not apply the `header`
   * section HTML — both are the wrong tool for this theme.
   */
  function refreshExpanseCart() {
    log('Expanse — skipping header section swap and Shopify.actions.openCart(); reprinting via cart:build', {
      theme: shopifyThemeLabel(),
    });
    try {
      document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    } catch (e) {
      logWarn('Expanse cart:build dispatch failed', e);
    }
    try {
      if (window.jQuery) {
        window.jQuery(document).trigger('cart:build');
      }
    } catch (e) {
      logWarn('Expanse jQuery cart:build trigger failed', e);
    }
    var cartApi = window.theme && window.theme.cart;
    if (cartApi && typeof cartApi.buildCart === 'function') {
      try {
        cartApi.buildCart();
      } catch (e) {
        logWarn('Expanse theme.cart.buildCart() failed', e);
      }
    }
  }

  // ---------- Context + section-id discovery (dynamic per theme, not hardcoded) ----------

  /**
   * Finds the live cart-drawer root element across the different markup conventions seen so far:
   * a `<cart-drawer>` custom element (Dawn / Ornate Home style), `#CartDrawer-Form`, `#cart-drawer`,
   * or an element carrying an explicit `class="cart-drawer"` (seen on STEPR's theme: a
   * `<cart-form class="cart-drawer" data-section-id="...">`, where the *tag* isn't `cart-drawer` at
   * all — only the class is). Tries the most specific/reliable signals first.
   */
  function findCartDrawerRoot() {
    var byTag = document.querySelector('cart-drawer');
    if (byTag) return byTag;
    var byId =
      document.getElementById('CartDrawer-Form') ||
      document.getElementById('cart-drawer') ||
      document.getElementById('HeaderCart');
    if (byId) return byId;
    var byClassWithSectionId = document.querySelector('[data-section-id].cart-drawer, .cart-drawer[data-ajax-update]');
    if (byClassWithSectionId) return byClassWithSectionId;
    // `.site-header__cart` covers the "West"/Prestige/Impulse-family (Out of the Sandbox) themes —
    // confirmed on usmedrehab.com, whose drawer is `<div id="HeaderCart" class="site-header__drawer
    // site-header__cart">`, not Dawn's `cart-drawer`/`#CartDrawer-Form` convention.
    var byClass = document.querySelector('.cart-drawer, .site-header__cart');
    if (byClass) return byClass;
    return null;
  }

  /**
   * Dawn (and Dawn-descended themes) use a single `cart-drawer` section that bundles line items,
   * footer, and the drawer element itself. They typically do NOT put `data-id` on
   * `#CartDrawer-CartItems`, and the live DOM often has no `#shopify-section-cart-drawer` wrapper
   * (only the detached drawer element). Requesting separate `cart-drawer-items` / `cart-drawer-footer`
   * section ids returns null from Section Rendering. Despite the name, this now covers any theme
   * whose drawer bundles everything into one section — not literally only Dawn (see
   * `findCartDrawerRoot`'s doc comment for the STEPR `<cart-form class="cart-drawer">` case).
   */
  function isDawnCartDrawerTheme() {
    return !!findCartDrawerRoot();
  }

  function getCartContext() {
    if (findCartDrawerRoot()) {
      return 'drawer';
    }
    return 'page';
  }

  function sectionIdFromElement(el) {
    if (!el) return null;
    var section = el.closest('[id^="shopify-section-"]');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function getCartItemsSectionId(context) {
    if (context === 'drawer') {
      // Dawn bundles items inside the cart-drawer section — never request a separate items section.
      if (isDawnCartDrawerTheme()) {
        return null;
      }

      var el = document.querySelector('#CartDrawer-CartItems[data-id$="__cart-items"]');
      if (el) return el.getAttribute('data-id');

      var drawerEl = document.querySelector('cart-drawer') || document.querySelector('.cart-drawer__line-items');
      var fromDrawer = sectionIdFromElement(drawerEl);
      if (fromDrawer) return fromDrawer;

      return 'cart-drawer-items';
    }

    var pageEl = document.querySelector('.cart__items[data-id$="__cart-items"]');
    if (pageEl) return pageEl.getAttribute('data-id');

    var sections = document.querySelectorAll('div[id^="shopify-section-"].shopify-section');
    for (var i = 0; i < sections.length; i++) {
      if (sections[i].querySelector('.cart-item, [data-cart-item]')) {
        return sections[i].id.replace('shopify-section-', '');
      }
    }
    return 'cart-items';
  }

  function getCartFooterSectionId(context) {
    if (context === 'drawer') {
      // Dawn bundles footer inside the cart-drawer section — never request a separate footer section.
      if (isDawnCartDrawerTheme()) {
        return null;
      }

      var el = document.querySelector('#CartDrawer-Footer[data-id$="__cart-footer"]');
      if (el) return el.getAttribute('data-id');

      var drawerEl = document.querySelector('cart-drawer');
      var fromDrawer = sectionIdFromElement(drawerEl);
      if (fromDrawer) return fromDrawer;

      return 'cart-drawer-footer';
    }

    var sections = document.querySelectorAll('div[id^="shopify-section-"].shopify-section');
    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      var hasCheckoutButton = section.querySelector('[name="checkout"], a[href*="/checkout"], form[action$="/checkout"]');
      var hasCartTotals = section.querySelector('[class*="cart__totals"], [class*="cart-total"]');
      if (hasCheckoutButton || hasCartTotals) {
        return section.id.replace('shopify-section-', '');
      }
    }
    return 'cart-footer';
  }

  /**
   * The section wrapping the cart-drawer element itself, not the items/footer inside it. Prefers an
   * explicit `data-section-id` attribute on the element when present (a theme-declared signal, seen
   * on STEPR's `<cart-form data-section-id="...">` — no DOM traversal needed, and more reliable than
   * inferring it) before falling back to `.closest('[id^="shopify-section-"]')`.
   */
  function getCartDrawerSectionId() {
    var drawerEl = findCartDrawerRoot();
    if (drawerEl) {
      var explicit = drawerEl.getAttribute && drawerEl.getAttribute('data-section-id');
      if (explicit) return explicit;
      var fromDrawer = sectionIdFromElement(drawerEl);
      if (fromDrawer) return fromDrawer;
    }
    return 'cart-drawer';
  }

  /** Header cart-count bubble — usually its own global section, but themes rename/nest it. */
  function getCartIconBubbleSectionId() {
    var el = document.querySelector('cart-icon-bubble, #cart-icon-bubble, .cart-count-bubble');
    var fromEl = sectionIdFromElement(el);
    if (fromEl) return fromEl;
    return 'cart-icon-bubble';
  }

  function uniqueSectionIds(ids) {
    var seen = {};
    var out = [];
    for (var i = 0; i < ids.length; i++) {
      var id = ids[i];
      if (!id || seen[id]) continue;
      seen[id] = true;
      out.push(id);
    }
    return out;
  }

  /** Same shape the legacy prosuro-js-core-runtime.js used: items, drawer, footer, icon bubble. */
  function buildSectionsList(context) {
    if (isExpanseTheme()) {
      log('Expanse — not requesting header/cart sections (native cart:build reprints the popup)');
      return [];
    }

    if (context === 'drawer' && isDawnCartDrawerTheme()) {
      var dawnIds = uniqueSectionIds([getCartDrawerSectionId(), getCartIconBubbleSectionId()]);
      log('Dawn cart drawer — requesting unified sections', dawnIds);
      return dawnIds;
    }

    return uniqueSectionIds([
      getCartItemsSectionId(context),
      context === 'drawer' ? getCartDrawerSectionId() : null,
      getCartFooterSectionId(context),
      getCartIconBubbleSectionId(),
    ]);
  }

  // ---------- Section HTML -> DOM swap (Tier 2) ----------

  function findCartDrawerNode(root) {
    if (!root) return null;
    var tag = root.tagName ? String(root.tagName).toLowerCase() : '';
    if (tag === 'cart-drawer') return root;
    if (root.classList && root.classList.contains('cart-drawer')) return root;
    if (!root.querySelector) return null;
    return (
      root.querySelector('cart-drawer') ||
      root.querySelector('[data-section-id].cart-drawer, .cart-drawer[data-ajax-update]') ||
      root.querySelector('.cart-drawer')
    );
  }

  function isCartDrawerSectionId(sectionId) {
    return sectionId === 'cart-drawer' || /(?:^|__)cart-drawer$/.test(String(sectionId));
  }

  /**
   * Open/closed state doesn't always live on the drawer element itself — STEPR's theme puts
   * `aria-hidden` on an ancestor wrapper (`.cart-drawer-modal`) instead, so check that too.
   */
  function isCartDrawerOpen(drawerEl) {
    if (!drawerEl) return false;
    if (drawerEl.classList.contains('active')) return true;
    if (drawerEl.classList.contains('is-open')) return true;
    // `is-active` is the open-state class on usmedrehab.com's theme: `#HeaderCart.site-header__cart
    // is-active`. Without this, isCartDrawerOpen() never recognizes it as open on that theme.
    if (drawerEl.classList.contains('is-active')) return true;
    if (drawerEl.hasAttribute('open')) return true;
    var ariaHiddenAncestor = drawerEl.closest ? drawerEl.closest('[aria-hidden]') : null;
    if (ariaHiddenAncestor && ariaHiddenAncestor.getAttribute('aria-hidden') === 'false') return true;
    var inner = drawerEl.querySelector('.drawer__inner, #CartDrawer, .cart-drawer');
    if (inner && inner.getAttribute('tabindex') === '-1') {
      var overlay = drawerEl.querySelector('.cart-drawer__overlay, #CartDrawer-Overlay');
      if (overlay && overlay.classList.contains('is-visible')) return true;
    }
    return false;
  }

  function reopenCartDrawerIfNeeded(drawerEl, wasOpen) {
    if (!drawerEl || !wasOpen) return;
    if (typeof drawerEl.open === 'function') {
      try {
        drawerEl.open();
        return;
      } catch (_) {}
    }
    var ariaHiddenAncestor = drawerEl.closest ? drawerEl.closest('[aria-hidden]') : null;
    if (ariaHiddenAncestor) {
      ariaHiddenAncestor.setAttribute('aria-hidden', 'false');
      return;
    }
    drawerEl.classList.add('active');
  }

  /**
   * Some themes render the cart-drawer element outside `#shopify-section-<id>` in the live DOM.
   * Section Rendering still returns the section's HTML (wrapper + drawer). Patch the live drawer
   * element directly when the section wrapper is missing.
   */
  function swapDawnCartDrawerSection(sectionId, html, touchedElementIds) {
    if (!isCartDrawerSectionId(sectionId)) return false;

    var liveDrawer = findCartDrawerRoot();
    if (!liveDrawer) return false;

    var tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.trim();
    var responseRoot = tempDiv.firstElementChild;
    var responseDrawer = findCartDrawerNode(responseRoot);
    if (!responseDrawer) return false;

    if (touchedElementIds['cart-drawer']) {
      logWarn('skipping cart-drawer swap (already swapped this batch)', sectionId);
      return true;
    }

    var wasOpen = isCartDrawerOpen(liveDrawer);
    liveDrawer.outerHTML = responseDrawer.outerHTML;
    touchedElementIds['cart-drawer'] = true;

    var newDrawer = findCartDrawerRoot();
    reopenCartDrawerIfNeeded(newDrawer, wasOpen);
    log('applied cart-drawer section via element swap', sectionId, { wasOpen: wasOpen });
    return true;
  }

  /**
   * `touchedElementIds` is shared across every section in one `applySections` call. Some themes
   * register more than one section under IDs whose *inner* content shares the same live element id
   * (e.g. a currently-mounted "sections--<id>__cart-drawer" section and an unrelated, differently
   * templated "cart-drawer" section both wrapping a `<cart-drawer id="cart-drawer">`). Section
   * Rendering happily returns both if requested, and naively applying both in sequence means the
   * second overwrites the first's freshly-opened drawer with unrelated default-state markup — the
   * "opens for a split second then disappears" symptom. Once an element id has been swapped in this
   * batch, skip any later section that would touch it again rather than guessing which is "right".
   */
  function swapOneSection(sectionId, html, touchedElementIds) {
    if (!html) return;

    if (isDawnCartDrawerTheme() && isCartDrawerSectionId(sectionId)) {
      if (swapDawnCartDrawerSection(sectionId, html, touchedElementIds)) {
        return;
      }
    }

    var targetId = 'shopify-section-' + sectionId;
    var targetElement = document.getElementById(targetId);

    var tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.trim();
    var responseRoot = tempDiv.firstElementChild;
    if (!responseRoot) return;

    // A section's real content sometimes lives elsewhere in the DOM (a custom element theme JS
    // moved to be a direct child of <body>). If the nominal section wrapper no longer contains
    // that element, patch it directly instead of the (stale) wrapper.
    var contentElementId = null;
    var contentElementHtml = html;
    if (responseRoot.id === targetId) {
      var responseDrawer = findCartDrawerNode(responseRoot);
      if (responseDrawer) {
        contentElementHtml = responseDrawer.outerHTML;
      } else if (responseRoot.firstElementChild && responseRoot.firstElementChild.id) {
        contentElementId = responseRoot.firstElementChild.id;
        contentElementHtml = responseRoot.firstElementChild.outerHTML;
      }
    } else if (responseRoot.id) {
      contentElementId = responseRoot.id;
    }

    if (contentElementId) {
      if (touchedElementIds[contentElementId]) {
        logWarn(
          'skipping section (would overwrite an element already swapped this batch, likely a ' +
            'differently-templated section colliding on the same live element id)',
          sectionId,
          contentElementId,
        );
        return;
      }
      var activeElement = document.getElementById(contentElementId);
      if (activeElement && (!targetElement || !targetElement.contains(activeElement))) {
        activeElement.outerHTML = contentElementHtml;
        touchedElementIds[contentElementId] = true;
        return;
      }
    }

    if (!targetElement) {
      if (swapDawnCartDrawerSection(sectionId, html, touchedElementIds)) {
        return;
      }
      logWarn('no target element found for section', sectionId);
      return;
    }
    if (touchedElementIds[targetId]) {
      logWarn('skipping section (target element already swapped this batch)', sectionId);
      return;
    }
    if (responseRoot.id === targetId) {
      targetElement.outerHTML = html;
    } else {
      targetElement.innerHTML = html;
    }
    touchedElementIds[targetId] = true;
  }

  function applySections(sections) {
    if (!sections) return;
    var touchedElementIds = {};
    for (var sectionId in sections) {
      if (!Object.prototype.hasOwnProperty.call(sections, sectionId)) continue;
      try {
        swapOneSection(sectionId, sections[sectionId], touchedElementIds);
      } catch (e) {
        logWarn('failed to apply section', sectionId, e);
      }
    }
    requestProsuroCartMarkerRescan();
  }

  function requestProsuroCartMarkerRescan() {
    try {
      if (window.ProsuroCartActions && typeof window.ProsuroCartActions.scheduleScan === 'function') {
        window.ProsuroCartActions.scheduleScan('cart-drawer-sync:applySections');
        return;
      }
      document.dispatchEvent(new CustomEvent('prosuro:cart:markers:rescan', { bubbles: true }));
    } catch (e) {
      logWarn('requestProsuroCartMarkerRescan failed (non-blocking)', e);
    }
  }

  // ---------- Theme's own native refresh, when present (Tier 0 — preferred over our manual swap) ----------

  /**
   * Many Dawn-descended themes ship a `<cart-drawer>` custom element with its own `refresh()` that
   * re-fetches `?sections=...` (computing the real section id itself, e.g.
   * `this.closest('.shopify-section').id`) and patches its own DOM. Several of these themes ALSO
   * independently patch `window.fetch`/`XMLHttpRequest` to call that same `refresh()` any time they
   * see a `/cart/add`-ish request resolve — completely unaware of, and unsynchronized with, our own
   * chain-add. That reaction can fire on the *first* (main-product) POST alone, well before our
   * plan-line chain POST has even started, so it reads and renders stale (pre-chain) cart state —
   * then, whenever it happens to land relative to our own swap, it can stomp a correct render with
   * that stale one. That's the "opens for a split second then reverts" symptom.
   *
   * Rather than trying to race or suppress the theme's own watcher, call its own `refresh()`
   * ourselves, deliberately, after our chain is fully done — so ours is the one that runs last and
   * wins, using the theme's own (already correct, already tested) render path instead of our manual
   * section swap. Falls back to that manual swap only when no such element/method exists.
   */
  function findThemeCartDrawerRefresh() {
    var drawer = findCartDrawerRoot();
    if (drawer && typeof drawer.refresh === 'function') return drawer;
    return null;
  }

  // ---------- Open the drawer/page ----------

  /**
   * Tier 0: a theme-native "open the drawer" signal, when this specific theme framework is
   * detected (fingerprinted by `theme.settings.after_add_to_cart`, the same property
   * `prosuro-cart-add-nested.js`'s `ensureThemeDrawerModeForced` checks). Confirmed field-testing
   * (STEPR/getstepr.com) that this theme's own `theme.addedToCartHandler` opens its drawer by
   * dispatching exactly this event — reusing it ourselves is more reliable than
   * `Shopify.actions.openCart()` below, whose own unconfigured default recognizer was observed,
   * field-testing, to fall back to a hard `/cart` redirect once our own `applySections()` had
   * already replaced the live drawer element `applySections` swaps into — its cached reference to
   * the pre-swap element goes stale, it "finds none," and the docs' own stated fallback ("navigates
   * to /cart when it finds none") kicks in. This event dispatch has no such dependency on a cached
   * element reference. Not a fix for that general risk class on OTHER themes — see the
   * open-before-swap ordering in `refreshAndOpenCart` for that.
   */
  function openCartDrawer() {
    if (window.theme && window.theme.settings && 'after_add_to_cart' in window.theme.settings) {
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false, detail: { openDrawer: true } }),
        );
        log('opened cart via theme:cartchanged (theme-native event; more reliable than Shopify.actions.openCart on this theme)');
        return Promise.resolve();
      } catch (e) {
        logWarn('theme:cartchanged dispatch failed, falling back to Shopify.actions.openCart()', e);
      }
    }

    // Tier 0.5: confirmed field-testing (usmedrehab.com, West/Prestige-family theme —
    // `#HeaderCart`/`.site-header__cart`, no `theme.settings.after_add_to_cart` for Tier 0 above to
    // key off) that Shopify.actions.openCart() hard-navigates to `/cart` on THIS theme
    // UNCONDITIONALLY — not only as a documented "couldn't find a recognized drawer" fallback, but
    // every time it's called, regardless of whether a drawer is already open. The theme's own
    // handler already opens `#HeaderCart` natively on the main-product add, before this plan
    // chain-add's refreshAndOpenCart() even runs, so Tier 1 below was never needed here anyway.
    // Scoped narrowly to this theme's own markup signature (not "any recognized drawer") since
    // `Shopify.actions.openCart()` may still be relied on correctly by other themes sharing this
    // extension — broadening this guard to every theme `findCartDrawerRoot()` matches is an
    // untested, wider behavior change this fix isn't making.
    if (!isExpanseTheme() && (document.getElementById('HeaderCart') || document.querySelector('.site-header__cart'))) {
      log(
        'West/Prestige-family theme detected — skipping Shopify.actions.openCart() (confirmed unsafe: hard-redirects to /cart on this theme)',
        { theme: shopifyThemeLabel() },
      );
      return Promise.resolve();
    }

    if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
      log('opening cart via Shopify.actions.openCart()');
      return window.Shopify.actions.openCart().catch(function (e) {
        logWarn('Shopify.actions.openCart() rejected', e);
      });
    }

    logWarn(
      'Shopify.actions.openCart not available on this theme — sections were still swapped into the ' +
        'DOM above, but nothing here knows how to reveal this theme\'s drawer yet.',
    );
    return Promise.resolve();
  }

  // ---------- Best-effort notify ----------

  /**
   * Dispatches a `shopify:cart:lines-update`-*shaped* event so any listener a theme has wired for
   * the standard event picks up the change. This is a best-effort, duck-typed approximation, not a
   * real `CartLinesUpdateEvent` instance — that class lives in `@shopify/standard-events`, an npm
   * package, and this file is a plain classic script with no bundler. A theme whose listener checks
   * `instanceof CartLinesUpdateEvent` won't recognize this; one that just reads the properties will.
   * Never blocks or throws out of `refreshAndOpenCart` if this fails.
   */
  function notifyCartChanged(detail) {
    try {
      var evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      evt.action = 'add';
      evt.context = 'standard-action';
      evt.lines = (detail && detail.lines) || [];
      evt.promise = Promise.resolve({ cart: (detail && detail.cart) || null });
      document.dispatchEvent(evt);
    } catch (e) {
      logWarn('notifyCartChanged failed (non-blocking)', e);
    }
  }

  /**
   * Some themes (found on both Ornate Home and STEPR — likely a shared third-party theme
   * framework, given the matching `cc-`-prefixed classes) do their own per-line DOM diffing on
   * refresh (`theme.mergeNodes` on STEPR's theme), keyed by a `data-merge-cache` value built from
   * that line's own variant/key/quantity/price. None of those inputs change when a sibling plan
   * line gets linked to a product line via `parent_relationship` — so the theme's own diff sees no
   * change for that line and reuses its stale, already-rendered HTML (e.g. an "Add Protection"
   * marker that should have disappeared), even though the freshly-fetched section HTML is
   * correct. Confirmed field-testing on STEPR: a full page reload "fixes" it because that's a real
   * server render with no diff/cache logic involved at all — the fetched section content was
   * always correct, only the merge step was skipping it.
   *
   * Fix: strip `data-merge-cache` from the specific parent line the plan just attached to, before
   * the theme's own refresh runs. The merge condition treats a missing/mismatched cache value as
   * "must update," so this forces a real re-render for just that one line.
   */
  function invalidateMergeCacheForLine(lineKey) {
    if (!lineKey) return;
    try {
      var lineEl = document.querySelector('[data-key="' + lineKey + '"]');
      var mergeItem = lineEl && lineEl.closest ? lineEl.closest('[data-merge-cache]') : null;
      if (mergeItem) {
        mergeItem.removeAttribute('data-merge-cache');
        log('invalidated stale data-merge-cache for parent line', lineKey);
      }
    } catch (e) {
      logWarn('invalidateMergeCacheForLine failed (non-blocking)', e);
    }
  }

  // ---------- Orchestration ----------

  /**
   * Call after the plan-line chain POST succeeds. `options.sections` is the `sections` object from
   * that response (only present if the caller requested `sections` on the POST body via
   * `buildSectionsList`). `options.cart` is the raw chain response, passed through to
   * `notifyCartChanged` for any listener that wants it. `options.parentLineKey` is the cart line
   * the plan(s) just attached to — used to invalidate a theme's own stale merge-cache for it, if
   * present.
   */
  function refreshAndOpenCart(options) {
    var opts = options || {};
    if (isExpanseTheme()) {
      invalidateMergeCacheForLine(opts.parentLineKey);
      notifyCartChanged({ lines: opts.lines, cart: opts.cart });
      refreshExpanseCart();
      return Promise.resolve();
    }
    invalidateMergeCacheForLine(opts.parentLineKey);
    notifyCartChanged({ lines: opts.lines, cart: opts.cart });

    // Open BEFORE swapping content into place, not after. `applySections`'s Tier 2 swap replaces
    // the live drawer element via `outerHTML`; doing that before opening can leave
    // `Shopify.actions.openCart()`'s own default recognizer holding a reference to the now-detached
    // pre-swap element — confirmed field-testing (STEPR) to fall back to a hard `/cart` redirect
    // when that happens (see `openCartDrawer`'s doc comment for the full trace). `applySections`
    // already tracks and restores open state across its own swap
    // (`isCartDrawerOpen`/`reopenCartDrawerIfNeeded`), so swapping after opening is safe — worst
    // case is a brief flash of pre-swap content while the real content lands a moment later.
    return openCartDrawer().then(function () {
      var themeDrawer = findThemeCartDrawerRefresh();
      if (themeDrawer) {
        log('theme cart-drawer.refresh() found — using it instead of our own manual section swap');
        return Promise.resolve(themeDrawer.refresh(true)).catch(function (e) {
          logWarn('theme cart-drawer.refresh(true) rejected; falling back to manual section swap', e);
          if (opts.sections) applySections(opts.sections);
        });
      }
      if (opts.sections) {
        applySections(opts.sections);
      } else {
        logWarn('refreshAndOpenCart called with no theme refresh() and no sections — nothing to swap');
      }
    });
  }

  window.ProsuroCartDrawerSync = {
    getCartContext: getCartContext,
    isDawnCartDrawerTheme: isDawnCartDrawerTheme,
    getCartItemsSectionId: getCartItemsSectionId,
    getCartFooterSectionId: getCartFooterSectionId,
    buildSectionsList: buildSectionsList,
    applySections: applySections,
    openCartDrawer: openCartDrawer,
    notifyCartChanged: notifyCartChanged,
    refreshAndOpenCart: refreshAndOpenCart,
  };

  log('module loaded');
})();
